import CompanyMobileActionFab from "@/components/company/company-mobile-action-fab";
import CompanyMobileBody from "@/components/company/company-mobile-body";
import CompanyMobileLoading, {
  CompanyMobileLoadingBanner,
} from "@/components/company/company-mobile-loading";
import { CompanyEnrichmentBanner } from "@/components/company/company-preview-panel-content";
import {
  CompanyBudgetSpentAlert,
  CompanyErrorMessage,
} from "@/components/company/company-run-alerts";
import {
  CompanySectionsContext,
  toCompanySectionsState,
} from "@/contexts/company-sections-context";
import { useMobileHeader } from "@/hooks/useMobileHeader";
import { useCompanyEnrichment } from "@/hooks/useCompanyEnrichment";
import { useCompanyPage, useCompanyPageErrorToast } from "@/hooks/useCompanyPage";
import { companyPageProgress } from "@/stores/company-page-store";
import { useTranslation } from "@/hooks/useTranslation";
import { setWindowTitle } from "@/lib/window-title";
import { UserDTO } from "@shaarp/common";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useBackOr } from "@/hooks/useBackOr";

export default function CompanyMobilePage() {
  const { t } = useTranslation();

  const { companyId = "" } = useParams();

  const page = useCompanyPage(companyId);
  const { company, done } = page;
  const progress = companyPageProgress(page);
  const isLoadingReady = company === null && !done;
  useCompanyPageErrorToast(page.error, company !== null);
  const { sections, budgetSpent } = page;
  const { isEnriching } = useCompanyEnrichment(companyId || null);
  // The enrichment banner is offered once the page's own run is over — a
  // second stream started meanwhile would hit the enrichment lock and freeze
  // the page on a stale row —, then stays through the run its tap restarts,
  // to show how it ended. A run joined here, started from the preview panel,
  // offers it only once it ended.
  const loadedCompanyId = company?.id ?? null;
  const [bannerOfferedFor, setBannerOfferedFor] = useState<string | null>(
    null,
  );
  const [bannerRunFor, setBannerRunFor] = useState<string | null>(null);
  if (done && loadedCompanyId && bannerOfferedFor !== loadedCompanyId) {
    setBannerOfferedFor(loadedCompanyId);
  }
  const isBannerOffered =
    loadedCompanyId !== null && (done || bannerOfferedFor === loadedCompanyId);
  if (isEnriching && isBannerOffered && bannerRunFor !== loadedCompanyId) {
    setBannerRunFor(loadedCompanyId);
  }
  // The banner tells how its own run ended, the spent budget included.
  const hasBannerRun = loadedCompanyId !== null && bannerRunFor === loadedCompanyId;
  const sectionsState = useMemo(
    () => toCompanySectionsState({ sections, done, budgetSpent }),
    [sections, done, budgetSpent],
  );
  const [assignedUser, setAssignedUser] = useState<UserDTO | null>(null);
  const [isActionFabExpanded, setIsActionFabExpanded] = useState<boolean>(false);

  useEffect(() => {
    setWindowTitle(company?.name || t("company.defaults.companyTitle"));
  }, [company, t]);

  // A company page is a drill-down: back to where it was opened from, or to
  // the chat when it was opened from a link.
  const goBack = useBackOr("/chat");
  useMobileHeader({
    title: company?.name ?? t("company.defaults.companyTitle"),
    onBack: goBack,
  });

  if (isLoadingReady) {
    return <CompanyMobileLoading progress={progress} />;
  }

  // An error after the stored company was painted keeps the page (the toast
  // reports it); the error screen is for a company that never arrived.
  if (!company) {
    return <CompanyErrorMessage error={page.error} className="pt-6" />;
  }

  // The bottom padding leaves room for the action button pinned above the
  // home indicator.
  return (
    <div className="flex flex-col pb-[calc(var(--mobile-bottom-inset)+5rem)]">
      {!done && !isBannerOffered && (
        <div className="px-4 pt-4">
          <CompanyMobileLoadingBanner progress={progress} />
        </div>
      )}
      {budgetSpent && !hasBannerRun && (
        <div className="px-4 pt-4">
          <CompanyBudgetSpentAlert />
        </div>
      )}
      <CompanySectionsContext value={sectionsState}>
        <CompanyMobileBody company={company} />
      </CompanySectionsContext>
      {isBannerOffered && (
        <CompanyEnrichmentBanner
          key={company.id}
          company={company}
          hideFadeOverlay
        />
      )}
      <CompanyMobileActionFab
        company={company}
        assignedUser={assignedUser}
        onAssignedUserChange={setAssignedUser}
        expanded={isActionFabExpanded}
        onExpandedChange={setIsActionFabExpanded}
      />
    </div>
  );
}
